import { firestore, firestoreConstructors, storage } from '../plugins/firebase'
import { store } from '../store'
import i18n from './i18n'
import getLanguage from './get-language'
import { parseDevice, parseCampaignData, getLatestClonedDevices, parseField, getLatestClonedFields, parseCrop } from './helpers'
import _uniq from 'lodash/uniq'
import flatten from 'lodash/flatten'
import _some from 'lodash/some'
import { getAuthCurrentUser } from '@/plugins/firebase'
import { identifyUser, setUserInfo } from './track-events'
import { USER_ROLES, WEBHOOKS_PATHS, HTTP_STATUS } from '../constants/const'
import { getOrganizationDevices } from './crudOperations/read'
import { fieldSurveyRefsToIds } from './fieldSurveysRouting'

async function getOrganizationUsers (organizationId, db) {
  const orgUsersList = await db.collection('app/v1/users').where('organization', '==', db.collection('app/v1/organizations').doc(organizationId)).get()
  const users = orgUsersList.docs.map(user => ({
    id: user.id,
    name: user.data().name,
    lastName: user.data().lastName || ''
  }))
  return users
}

async function getOrganizationFields (organizationId, db) {
  const orgFieldsList = await db.collection('app/v1/fields').where('organization', '==', db.collection('app/v1/organizations').doc(organizationId)).get()
  const fields = orgFieldsList.docs.map(field => parseField(field.id, field.data()))
  return fields
}

async function getCrops (fields, db) {
  const cropsIds = _uniq(flatten(fields.map(field => field.crops || [])))
  if (cropsIds.length === 0) return []

  const cropsQueries = []
  const cropsIdsCopy = [...cropsIds]
  while (cropsIdsCopy.length) {
    const cropsIdsBatch = cropsIdsCopy.splice(0, 10)
    cropsQueries.push(
      db.collection('app/v1/crops').where(firestoreConstructors.FieldPath.documentId(), 'in', cropsIdsBatch).get()
    )
  }

  const cropsQueriesSnapshot = await Promise.all(cropsQueries)
  const crops = cropsQueriesSnapshot.reduce((cropsAcc, snapshot) => {
    snapshot.forEach(doc => cropsAcc.push(doc))
    return cropsAcc
  }, [])

  // Process all crops at once
  return crops
    .map(crop => {
      const cropData = crop.data()
      if (!cropData) return null

      return parseCrop(crop.id, cropData)
    })
    .filter(Boolean)
}

/**
 * Gets the active campaign ID for the user
 * @param {Array} campaigns - User campaigns
 * @returns {string|null}
 */
function getActiveCampaignId (campaigns) {
  // Use the campaign selected in the store (user's current selection)
  const storeActiveCampaign = store.getters.activeCampaign
  if (storeActiveCampaign) {
    return storeActiveCampaign
  }

  // If no campaign is selected, use the first available campaign
  if (campaigns && campaigns.length > 0) {
    store.commit('setActiveCampaign', campaigns[0].id)
    return campaigns[0].id
  }

  return null
}

function getRoleLabel (roles, userData) {
  const { isAdmin, audits } = userData
  if (isAdmin) {
    return USER_ROLES.ADMIN
  }
  if (audits) {
    return USER_ROLES.AUDITS
  }
  switch (true) {
    case roles.owner:
      return USER_ROLES.OWNER
    case roles.manager:
      return USER_ROLES.MANAGER
    case roles.fieldman:
      return USER_ROLES.FIELDMAN
    default:
      return USER_ROLES.FARMHAND
  }
}

async function identifyUserToTrackData (user, organization, roles) {
  identifyUser(user.id)
  setUserInfo({
    id: user.id,
    email: user.data().email,
    name: `${user.data().name} ${user.data().lastName || ''}`,
    orgName: organization.data().name,
    role: getRoleLabel(roles, user.data())
  })
}

/**
 * Updates the last login date for a user in Hubspot
 * @param {Object} userDoc - Firestore document containing user data
 * @param {boolean} isImpersonated - Whether the user is being impersonated
 * @param {Object} authUser - Firebase auth user
 * @returns {Promise<void>}
 */
async function updateLastLoginDate (userDoc, isImpersonated, authUser) {
  if (!isImpersonated && userDoc.data().hubspotId) {
    try {
      const response = await fetch(`${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.UPDATE_CONTACT_DATA}`, {
        method: 'PATCH',
        body: JSON.stringify({
          id: userDoc.data().hubspotId,
          properties: {
            'abm_reports_last_access': new Date().getTime()
          }
        }),
        headers: {
          authorization: process.env.WEBHOOKS_BASIC_TOKEN,
          'Content-Type': 'application/json',
          'auth-token': await authUser.getIdToken()
        }
      })
      if (response.status !== HTTP_STATUS.OK) {
        console.log('Error updating last login date', response.status)
      }
    } catch (err) {
      console.log('Error updating last login date', err)
    }
  }
}

export default async function (userId) {
  try {
    // Validate auth login
    const authUser = await getAuthCurrentUser()
    if (!authUser) {
      store.dispatch('logout')
      return {error: true}
    }

    const db = await firestore
    const [userDoc, userCampaignsSettingsRefs] = await Promise.all([
      db.collection('app/v1/users').doc(userId).get(),
      db.collection(`app/v1/users/${userId}/campaigns`).get()
    ])
    const userDocData = userDoc.data()
    const userCampaignsSettings = userCampaignsSettingsRefs.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      devices: doc.data().devices ? doc.data().devices.map(device => device.id) : []
    }))

    // Check visualization access
    const impersonated = store.getters.impersonatedUserId
    const hasFieldSurveysAccess = !!(userDocData.fieldSurveys && userDocData.fieldSurveys.length)
    if (!userDocData.isAdmin && !impersonated && (!userDocData.audits && !hasFieldSurveysAccess && !_some(userCampaignsSettings, campaign => campaign.editDataPermission) && !_some(userCampaignsSettings, campaign => campaign.reports))) {
      store.dispatch('logout')
      return {error: true}
    }

    // Get campaigns details
    const campaignsDetailQueries = []
    const campaignIds = userCampaignsSettings.map(campaign => campaign.id)
    while (campaignIds.length) {
      const campaignIdsBatch = campaignIds.splice(0, 10)
      const campaignDetailQuery = db.collection('app/v1/campaigns').where(firestoreConstructors.FieldPath.documentId(), 'in', campaignIdsBatch).get()
      campaignsDetailQueries.push(campaignDetailQuery)
    }
    const campaignsDetailQueriesSnapshot = await Promise.all(campaignsDetailQueries)
    const campaignsDetails = campaignsDetailQueriesSnapshot.reduce((campaignsAcc, snapshot) => {
      snapshot.forEach(doc => {campaignsAcc.push(parseCampaignData(doc))})
      return campaignsAcc
    }, [])

    const allDeviceIds = _uniq(flatten(campaignsDetails.map(campaign => campaign.devices || [])))
    const allFieldIds = _uniq(flatten(campaignsDetails.map(campaign => campaign.fields || [])))
    const allUserIds = _uniq(flatten(campaignsDetails.map(campaign => campaign.users || [])))

    const devicesQueries = []
    const deviceIdsCopy = [...allDeviceIds]
    while (deviceIdsCopy.length) {
      const devicesIdsBatch = deviceIdsCopy.splice(0, 10)
      const devicesQuery = db.collection('app/v1/devices').where(firestoreConstructors.FieldPath.documentId(), 'in', devicesIdsBatch).get()
      devicesQueries.push(devicesQuery)
    }

    const fieldsQueries = []
    const fieldIdsCopy = [...allFieldIds]
    while (fieldIdsCopy.length) {
      const fieldsIdsBatch = fieldIdsCopy.splice(0, 10)
      const fieldsQuery = db.collection('app/v1/fields').where(firestoreConstructors.FieldPath.documentId(), 'in', fieldsIdsBatch).get()
      fieldsQueries.push(fieldsQuery)
    }

    const usersQueries = []
    const userIdsCopy = [...allUserIds]
    while (userIdsCopy.length) {
      const usersIdsBatch = userIdsCopy.splice(0, 10)
      const usersQuery = db.collection('app/v1/users').where(firestoreConstructors.FieldPath.documentId(), 'in', usersIdsBatch).get()
      usersQueries.push(usersQuery)
    }

    const [devicesQueriesSnapshot, fieldsQueriesSnapshot, usersQueriesSnapshot] = await Promise.all([
      Promise.all(devicesQueries),
      Promise.all(fieldsQueries),
      Promise.all(usersQueries)
    ])

    const allDevices = devicesQueriesSnapshot.reduce((devicesAcc, snapshot) => {
      snapshot.forEach(doc => { devicesAcc.push(parseDevice(doc.id, doc.data())) })
      return devicesAcc
    }, [])

    const allFields = fieldsQueriesSnapshot.reduce((fieldsAcc, snapshot) => {
      snapshot.forEach(doc => { fieldsAcc.push(parseField(doc.id, doc.data())) })
      return fieldsAcc
    }, [])

    const allUsers = usersQueriesSnapshot.reduce((usersAcc, snapshot) => {
      snapshot.forEach(doc => { usersAcc.push(doc) })
      return usersAcc
    }, [])

    const usersCampaignsMap = new Map()
    // Create a map of users and their campaigns
    campaignsDetails.forEach(campaign => {
      if (campaign.users) {
        campaign.users.forEach(userId => {
          if (!usersCampaignsMap.has(userId)) {
            usersCampaignsMap.set(userId, new Set())
          }
          usersCampaignsMap.get(userId).add(campaign.id)
        })
      }
    })

    // Batch queries for user campaigns
    const userCampaignQueries = []
    for (const [userId, campaignIds] of usersCampaignsMap.entries()) {
      for (const campaignId of campaignIds) {
        userCampaignQueries.push(
          db.collection('app/v1/users').doc(userId).collection('campaigns').doc(campaignId).get()
        )
      }
    }

    // Execute all queries in parallel
    const userCampaignDocs = await Promise.all(userCampaignQueries)

    const devicesMap = new Map(allDevices.map(device => [device.id, device]))
    const fieldsMap = new Map(allFields.map(field => [field.id, field]))
    const usersMap = new Map(allUsers.map(user => [user.id, user]))
    const userCampaignMap = new Map()

    userCampaignDocs.forEach(doc => {
      const pathParts = doc.ref.path.split('/')
      const userId = pathParts[3]
      const campaignId = pathParts[5]
      userCampaignMap.set(`${userId}-${campaignId}`, doc.data())
    })

    campaignsDetails.forEach(campaign => {
      campaign.devices = (campaign.devices || []).map(id => devicesMap.get(id)).filter(Boolean)
      campaign.fields = (campaign.fields || []).map(id => fieldsMap.get(id)).filter(Boolean)

      campaign.users = (campaign.users || []).map(userId => {
        const user = usersMap.get(userId)
        if (!user) return null

        const userCampaignData = userCampaignMap.get(`${userId}-${campaign.id}`)
        const uData = user.data()
        let role = ''
        if (uData.isAdmin) {
          role = 'admin'
        } else if (!userCampaignData.roles && uData.audits) {
          role = 'audits'
        } else if (userCampaignData.roles && userCampaignData.roles.owner) {
          role = 'owner'
        } else if (userCampaignData.roles && userCampaignData.roles.manager) {
          role = 'manager'
        } else if (userCampaignData.roles && userCampaignData.roles.fieldman) {
          role = 'fieldman'
        } else if (userCampaignData.roles && userCampaignData.roles.farmhand) {
          role = 'farmhand'
        }

        return {
          id: user.id,
          name: uData.name,
          lastName: uData.lastName || '',
          email: uData.email,
          phone: uData.phone,
          reports: userCampaignData && userCampaignData.reports,
          devices: userCampaignData && userCampaignData.devices ? userCampaignData.devices.map(device => device.id) : [],
          role,
          editDataPermission: userCampaignData && userCampaignData.editDataPermission,
          editCampaignDataPermission: userCampaignData && userCampaignData.editCampaignDataPermission,
          photoUrl: uData.photoUrl,
          channels: uData.channels,
          settings: uData.settings,
          lastUpdatedDate: uData.lastUpdatedDate || '',
          audits: uData.audits
        }
      }).filter(Boolean)
    })

    const parsedUserDoc = {
      id: userId,
      photoUrl: userDocData.photoUrl,
      isAdmin: userDocData.isAdmin,
      name: userDocData.name,
      lastName: userDocData.lastName || '',
      userCampaignsSettings,
      campaigns: campaignsDetails,
      audits: userDocData.audits,
      fieldSurveyIds: fieldSurveyRefsToIds(userDocData.fieldSurveys)
    }
    store.commit('setUser', parsedUserDoc)

    if (parsedUserDoc && parsedUserDoc.photoUrl) {
      const imageRef = storage.ref(parsedUserDoc.photoUrl)
      imageRef.getDownloadURL().then((profilePic) => {
        store.commit('setUser', {
          ...parsedUserDoc,
          profilePic
        })
      })
    }

    if (userDocData.settings && userDocData.settings.timeZone) {
      if (userDocData.settings.useDeviceTimeZone) {
        store.commit('setTimezone', Intl.DateTimeFormat().resolvedOptions().timeZone)
      } else {
        store.commit('setTimezone', userDocData.settings.timeZone)
      }
    }
    const language = getLanguage(userDoc)
    i18n.locale = language

    // Sort campaigns by end date (toDate) in descending order to get the latest ones first
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Reset time to start of day for accurate comparison

    const sortedCampaigns = campaignsDetails
      .sort((a, b) => {
        // Use toDate if it exists, otherwise use estimatedStartDate
        const getDateA = (campaign) => {
          if (campaign.toDate) {
            return campaign.toDate.toDate ? campaign.toDate.toDate() : new Date(campaign.toDate)
          }
          return campaign.estimatedStartDate.toDate ? campaign.estimatedStartDate.toDate() : new Date(campaign.estimatedStartDate)
        }

        const getDateB = (campaign) => {
          if (campaign.toDate) {
            return campaign.toDate.toDate ? campaign.toDate.toDate() : new Date(campaign.toDate)
          }
          return campaign.estimatedStartDate.toDate ? campaign.estimatedStartDate.toDate() : new Date(campaign.estimatedStartDate)
        }

        const dateA = getDateA(a)
        const dateB = getDateB(b)

        return dateB.getTime() - dateA.getTime() // Descending order (latest first)
      })

    const newestCampaign = userCampaignsSettings.find(campaign => campaign.id === sortedCampaigns[0].id) || { roles: { }, editDataPermission: false, editCampaignDataPermission: false }

    // Storing data
    const organizationId = userDocData.organization.path.split('/').pop()
    store.commit('setOrganization', {id: organizationId})
    store.commit('setUserRoles', {
      ...newestCampaign.roles,
      isAdmin: userDocData.isAdmin,
      audits: userDocData.audits
    })

    const [org, organizationUsers, organizationFields, organizationDevices, appConstantsDoc, campaignConstantsDoc] = await Promise.all([
      db.collection('app/v1/organizations').doc(organizationId).get(),
      getOrganizationUsers(organizationId, db),
      getOrganizationFields(organizationId, db),
      getOrganizationDevices(organizationId, db),
      db.doc('app/v1').get(),
      db.doc('app/v1/constants/campaigns').get()
    ])

    const orgData = org.data()
    store.commit('setOrganization', {name: orgData.name, hubspotId: orgData.hubspotId})
    store.commit('setOrganizationUsers', organizationUsers)

    let processedOrganizationFields = getLatestClonedFields(organizationFields)
    store.commit('setOrganizationFields', processedOrganizationFields)

    let processedOrganizationDevices = getLatestClonedDevices(organizationDevices)
    store.commit('setOrganizationDevices', processedOrganizationDevices)

    store.commit('setDevUsers', appConstantsDoc.data().devUsers)
    store.commit('setGlobalConstants', campaignConstantsDoc.data())

    const campaignHasRequiredFields = campaign => !!(campaign.name && campaign.fromDate)

    const isCampaignActive = campaign => {
      if (!campaign.toDate) return true
      const endDate = campaign.toDate.toDate ? campaign.toDate.toDate() : new Date(campaign.toDate)
      endDate.setHours(23, 59, 59, 999)
      return today <= endDate
    }

    const mapCampaign = campaign => ({
      id: campaign.id,
      name: campaign.name,
      fromDate: campaign.fromDate,
      toDate: campaign.toDate
    })

    let availableCampaigns = sortedCampaigns
      .filter(campaign => campaignHasRequiredFields(campaign) && isCampaignActive(campaign))
      .map(mapCampaign)

    // If no active campaign exists, fall back to the most recently ended campaign so the user always sees at least one
    if (availableCampaigns.length === 0) {
      const lastEndedCampaign = sortedCampaigns
        .filter(campaignHasRequiredFields)
        .find(campaign => {
          if (!campaign.toDate) return false
          const endDate = campaign.toDate.toDate ? campaign.toDate.toDate() : new Date(campaign.toDate)
          endDate.setHours(23, 59, 59, 999)
          return today > endDate
        })
      if (lastEndedCampaign) {
        availableCampaigns = [mapCampaign(lastEndedCampaign)]
      }
    }

    store.commit('setAvailableCampaigns', availableCampaigns)

    // Use campaign-based structure
    const activeCampaignId = getActiveCampaignId(availableCampaigns)
    let campaignDevices, fields, campaignUsers

    if (activeCampaignId) {
      // Get the active campaign from the already processed campaigns
      const activeCampaign = campaignsDetails.find(campaign => campaign.id === activeCampaignId)
      if (activeCampaign) {
        // Use the devices and fields already loaded for this campaign
        campaignDevices = activeCampaign.devices || []
        fields = activeCampaign.fields || []
        campaignUsers = activeCampaign.users || []
      } else {
        campaignDevices = []
        fields = []
        campaignUsers = []
      }
    } else {
      // Fallback to empty arrays if no active campaign found
      campaignDevices = []
      fields = []
      campaignUsers = []
    }

    store.commit('setDevices', campaignDevices)
    store.commit('setFields', fields)
    store.commit('setUsers', campaignUsers)

    // Get crops asynchronously
    if (fields.length > 0) {
      getCrops(fields, db).then(crops => {
        store.commit('setCrops', crops)
      }).catch(err => {
        console.error('Error loading crops:', err)
      })
    } else {
      store.commit('setCrops', [])
    }

    // Parsing permissions
    if (userDocData.permissions) {
      const orgPermissions = {}
      const devicesQueries = []
      userDocData.permissions.forEach(permItem => {
        if (permItem.type === 'organization') {
          orgPermissions[permItem.id] = []
        } else if (permItem.type === 'device') {
          devicesQueries.push(db.collection('app/v1/devices').doc(permItem.id).get())
        }
      })

      const docs = await Promise.all(devicesQueries)
      const devPermissions = {}
      docs.forEach(doc => {
        const devData = doc.data()
        const orgId = devData.organization.path.split('/').pop()
        if (typeof orgPermissions[orgId] === 'undefined') {
          if (!devPermissions[orgId]) {
            devPermissions[orgId] = [doc.id]
          } else {
            devPermissions[orgId].push(doc.id)
          }
        }
      })
      store.commit('setUserPermissions', {...orgPermissions, ...devPermissions})
    } else {
      store.commit('setUserPermissions', {})
    }

    // Identify user in tracking apps
    identifyUserToTrackData(userDoc, org, newestCampaign.roles)

    // Update last login date
    updateLastLoginDate(userDoc, impersonated, authUser).catch(err => {
      console.error('Error updating last login date:', err)
    })

    return {error: false}
  } catch (err) {
    console.log('Loading Error:', err)
    return {error: true, errorData: err}
  }
}



// WEBPACK FOOTER //
// ./src/lib/getUserData.js